import React from 'react'
import styled from 'styled-components'

import type {HomepageV3Query} from '@/graphql-types'
import {Steps} from '@/sections/steps'
import {CardGridSlice} from '@/slices/card-grid'
import {ContentPlusBlocksSlice} from '@/slices/content-plus-blocks'
import {CustomerLogosWithCta} from '@/slices/customer-logos-cta'
import {FeatureBlockSlice} from '@/slices/feature-block'
import {FeatureGridSlice} from '@/slices/feature-grid'
import {GetStartedSlice} from '@/slices/get-started'
import {IntegrationsSlice} from '@/slices/integrations'
import {RatingsPlusHeadlineSlice} from '@/slices/ratings-plus-headline'
import {SocialProofSlice} from '@/slices/social-proof'
import {StatisticsSlice} from '@/slices/statistics'
import exhaustiveCheck from '@helios/shared/utils/exhaustive-check'
import {isEmptyObject} from '@helios/shared/utils/is-empty-object'
import mq from '@helios/shared/utils/media-query'

import {Hero} from './hero'
import {RotatingBanner, isAllowedBgColor, isAllowedSizeVariant} from './rotating-banner'
import {VideoTabs} from './video-tabs'
import {VideoWithAnimatedOutline} from './video-with-animated-outline'

type SliceData = HomepageV3Query['prismicHomepageV3']['data']['body'][number]

type Props = {
    slices: SliceData[]
}

export const SliceZone = ({slices}: Props) => {
    return (
        <>
            {slices.map((slice, i) => {
                // New slices added in Prismic will show up as empty objects
                // until we explicitly query content for them
                if (isEmptyObject(slice)) {
                    return null
                }

                if (!slice.__typename) {
                    throw new Error(`Missing __typename in slice: ${JSON.stringify(slice)}`)
                }

                const type = slice.__typename
                const key = `${type}-${i}`

                switch (type) {
                    case 'PrismicHomepageV3DataBodyHero':
                        return <Hero key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyFeatureBlock':
                        return <FeatureBlockSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyContentPlusBlocks':
                        return <ContentPlusBlocksSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyGetStarted':
                        return <GetStartedSlice key={key} slice={slice} dataTestId="get-started" />
                    case 'PrismicHomepageV3DataBodyStats':
                        return <StatisticsSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyCardGrid':
                        return <CardGridSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyIntegrations':
                        return <IntegrationsSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyCustomerLogosPlusCta':
                        return (
                            <CustomerLogosWithCta
                                key={key}
                                ctaText={slice.primary.cta_text}
                                ctaLink={slice.primary.cta_link}
                                title={slice.primary.title}
                                backgroundColor={slice.primary.background_color}
                                logos={slice.items.map(({logo}) => logo)}
                            />
                        )
                    case 'PrismicHomepageV3DataBodyRotatingBanner':
                        return (
                            <RotatingBanner
                                key={key}
                                items={slice.items.map(({text}) => text)}
                                ctaLink={slice.primary.cta_link}
                                ctaText={slice.primary.cta_text}
                                bgColor={
                                    isAllowedBgColor(slice.primary.bg_color)
                                        ? slice.primary.bg_color
                                        : 'pink'
                                }
                                sizeVariant={
                                    isAllowedSizeVariant(slice.primary.size_variant)
                                        ? slice.primary.size_variant
                                        : 'small'
                                }
                            />
                        )
                    case 'PrismicHomepageV3DataBodyVideoTabs':
                        const tabs = slice.items.map((item) => {
                            return {
                                ...item,
                                video: {
                                    mp4: item.video_mp4?.url,
                                    webm: item.video_webm?.url,
                                },
                            }
                        })

                        return (
                            <HiddenBelowMd key={key}>
                                <VideoTabs tabs={tabs} />
                            </HiddenBelowMd>
                        )
                    case 'PrismicHomepageV3DataBodyVideoWithOutline':
                        const content = {
                            section_title: slice.primary.title,
                            video_list: slice.items,
                        }

                        return <VideoWithAnimatedOutline key={key} data={content} />
                    case 'PrismicHomepageV3DataBodySocialProof':
                        return <SocialProofSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyRatingsPlusHeadline':
                        return <RatingsPlusHeadlineSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodyFeatureGrid':
                        return <FeatureGridSlice key={key} slice={slice} />
                    case 'PrismicHomepageV3DataBodySteps':
                        return (
                            <Steps
                                key={key}
                                title={slice.primary.title}
                                showCount={slice.primary.show_count}
                                items={slice.items}
                            />
                        )
                    default:
                        exhaustiveCheck(type)

                        throw new Error(`Unknown slice: ${JSON.stringify(slice)}`)
                }
            })}
        </>
    )
}

const HiddenBelowMd = styled.div`
    ${mq.max('md')} {
        display: none;
    }
`
